import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { webSocket, WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';
import { BehaviorSubject, Subject, Subscription, firstValueFrom } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';

import { BYPASS_INTERCEPTOR } from '@shared/constants/http-context-tokens';
import { UserInfoService } from 'src/app/profiles/commons/services/user-info/user-info.service';
import { environment } from '@environments/environment';

import { ChatbotConfig } from './chatbot.config';
import { ChatbotMapper } from './chatbot.mapper';
import {
	ChatMessageUI,
	ChatSendMessageRequest,
	ChatUploadResponse,
	ChatMessageResponse,
	ChatHistoryFrame,
	ChatConnectionState
} from './chatbot.models';

@Injectable({ providedIn: 'root' })
export class ChatbotWebSocketFactory {
	create(config: WebSocketSubjectConfig<unknown>): WebSocketSubject<unknown> {
		return webSocket<unknown>(config);
	}
}

@Injectable({
	providedIn: 'root'
})
export class ChatbotService implements OnDestroy {
	private messagesSubject = new BehaviorSubject<ChatMessageUI[]>([]);
	public readonly unreadNotification$ = new BehaviorSubject<boolean>(false);
	private lastNotificationCount = -1;
	private lastBotMessageId: string | null = null;
	public messages$ = this.messagesSubject.asObservable();

	private connectionStateSubject = new BehaviorSubject<ChatConnectionState>('disconnected');
	public connectionState$ = this.connectionStateSubject.asObservable();

	public sessionResetSubject = new Subject<void>();
	public sessionReset$ = this.sessionResetSubject.asObservable();

	private currentIdentityFingerprint: string | null = null;
	private userInfoSub: Subscription | null = null;

	private wsSubject: WebSocketSubject<unknown> | null = null;
	private wsSubscription: Subscription | null = null;

	private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	private intentionalClose = false;

	private connectionGeneration = 0;

	constructor(
		private readonly http: HttpClient,
		private readonly userInfoService: UserInfoService,
		private readonly wsFactory: ChatbotWebSocketFactory,
		private readonly router: Router,
		private readonly cookieService: CookieService
	) {
		this.userInfoSub = this.userInfoService.userInfo$.subscribe((user) => {
			if (user && localStorage.getItem('user_info')) {
				const tentativeFingerprint = this.identityFingerprint(user.username, user.telephone, user.documentNumber);

				if (
					this.currentIdentityFingerprint !== null &&
					this.currentIdentityFingerprint !== tentativeFingerprint
				) {
					const wasConnected =
						!!this.wsSubject ||
						this.connectionStateSubject.value === 'connecting' ||
						this.connectionStateSubject.value === 'reconnecting';
					this.teardownSession();
					if (wasConnected) {
						this.connect();
					}
				} else if (this.currentIdentityFingerprint === null) {
					this.connect();
				}
			}
		});
		this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
			if (!localStorage.getItem('user_info')) {
				this.teardownSession();
				this.currentIdentityFingerprint = null;
			}
		});
	}

	/**
	 * Single source of truth for the profile identity behind the socket.
	 * `connect()` compares this fingerprint to detect a profile switch, so it must
	 * be derived exactly like the value `establishWsConnection()` stores, otherwise
	 * every `connect()` call looks like a new profile and tears the session down.
	 */
	private identityFingerprint(username: string, rawPhone: string, rawDocument: string): string {
		const phone = ChatbotMapper.normalizePhoneNumber(rawPhone) || rawPhone;
		const document = ChatbotMapper.normalizeDocumentNumber(rawDocument) || rawDocument;
		return `${username}-${phone}-${document}`;
	}

	private get phone(): string {
		return ChatbotMapper.normalizePhoneNumber(this.userInfoService.userInfo?.telephone || '');
	}
	private get isLeader(): boolean {
		return false;
	}

	private getAuthorizationToken(): string | null {
		const authData = this.cookieService.get('authData');
		if (!authData) return null;

		try {
			const token = JSON.parse(authData)?.access_token;
			return typeof token === 'string' && token ? token : null;
		} catch (error) {
			console.error('[Chatbot] Failed to read authorization token from authData cookie', error);
			return null;
		}
	}

	private teardownSession(): void {
		this.disconnect();
		this.messagesSubject.next([]);
		this.sessionResetSubject.next();
		this.lastBotMessageId = null;
		this.lastNotificationCount = -1;
		this.unreadNotification$.next(false);
	}

	private isChatHistoryFrame(frame: unknown): frame is ChatHistoryFrame {
		if (!frame || typeof frame !== 'object') return false;
		const f = frame as Record<string, unknown>;

		if (typeof f['status'] !== 'boolean') return false;

		const validEvents = ['initial', 'update', 'refresh', 'error nro_document'];
		if (typeof f['event'] !== 'string' || !validEvents.includes(f['event'])) return false;

		if (f['data_history'] !== undefined && !Array.isArray(f['data_history'])) return false;

		return true;
	}

	public clearUnreadNotification(): void {
		this.unreadNotification$.next(false);
		this.lastNotificationCount = 0; // Reset just in case

		// Save the last read bot message ID so it survives logouts
		try {
			if (typeof localStorage !== 'undefined' && this.currentIdentityFingerprint) {
				const msgs = this.messagesSubject.getValue();
				if (msgs && msgs.length > 0) {
					const lastBotMsg = msgs.slice().reverse().find(m => m.sender === 'bot');
					if (lastBotMsg) {
						localStorage.setItem(`inchat_last_read_${this.currentIdentityFingerprint}`, lastBotMsg.id);
						this.lastBotMessageId = lastBotMsg.id; // Sync locally
					}
				}
			}
		} catch (e) {}
	}

	public connect(): void {
		const user = this.userInfoService.userInfo;
		if (!user || !user.documentNumber || !user.username) {
			this.connectionStateSubject.next('error');
			return;
		}

		const rawPhone = user.telephone;
		const tentativeFingerprint = this.identityFingerprint(user.username, rawPhone, user.documentNumber);

		if (
			this.currentIdentityFingerprint !== null &&
			this.currentIdentityFingerprint !== tentativeFingerprint
		) {
			this.teardownSession();
		}

		if (this.wsSubject || this.connectionStateSubject.value === 'error') {
			return;
		}

		this.intentionalClose = false;
		this.clearReconnectTimer();

		if (this.connectionStateSubject.value !== 'reconnecting') {
			this.connectionStateSubject.next('connecting');
		}

		this.http.get<any>(`${environment.URL_ADMIN}/user/username/${user.username}`).subscribe({
			next: (freshUser) => {
				const freshPhone = freshUser?.nroPhone || user.telephone;
				this.establishWsConnection(freshPhone, user.documentNumber);
			},
			error: (err) => {
				console.error(
					'[Chatbot] Failed to fetch fresh user data, falling back to local storage',
					err
				);
				this.establishWsConnection(user.telephone, user.documentNumber);
			}
		});
	}

	private establishWsConnection(rawPhone: string, documentNumber: string): void {
		const authorization = this.getAuthorizationToken();
		if (!authorization) {
			console.warn('[Chatbot] WebSocket connection skipped: access_token is not available');
			this.connectionStateSubject.next('disconnected');
			return;
		}

		const currentUsername = this.userInfoService.userInfo?.username || '';
		const normalizedPhone = ChatbotMapper.normalizePhoneNumber(rawPhone) || rawPhone;

		this.currentIdentityFingerprint = this.identityFingerprint(currentUsername, rawPhone, documentNumber);

		const params = new URLSearchParams({ token: ChatbotConfig.API_KEY });
		const separator = ChatbotConfig.WS_URL.includes('?') ? '&' : '?';
		const wsUrl = `${ChatbotConfig.WS_URL}${separator}${params.toString()}`;

		const currentGeneration = ++this.connectionGeneration;

		this.wsSubject = this.wsFactory.create({
			url: wsUrl,
			deserializer: (e: MessageEvent) => {
				try {
					return JSON.parse(e.data);
				} catch {
					return null;
				}
			},
			openObserver: {
				next: () => {
					if (this.connectionGeneration !== currentGeneration) return;
					this.connectionStateSubject.next('connected');
					this.wsSubject?.next({
						number: normalizedPhone,
						nro_document: documentNumber,
						authorization
					});
				}
			},
			closeObserver: {
				next: (e: CloseEvent) => {
					if (this.connectionGeneration !== currentGeneration) return;
					if (e.code === 4004) {
						this.handleInvalidDocument();
						return;
					}
					if (!this.intentionalClose) {
						this.scheduleReconnect();
					}
				}
			}
		});

		this.wsSubscription = this.wsSubject.subscribe({
			next: (frame: unknown) => {
				if (this.connectionGeneration !== currentGeneration) return;
				if (!this.isChatHistoryFrame(frame)) return;

				if (frame.event === 'error nro_document') {
					this.handleInvalidDocument();
					return;
				}

				const isHistoryEmpty = !frame.data_history || frame.data_history.length === 0;

				if (frame.event === 'update' && isHistoryEmpty) {
					return;
				}

				if ((frame.event === 'initial' || frame.event === 'refresh') && isHistoryEmpty) {
					this.messagesSubject.next([]);
					return;
				}

				let mappedMessages = ChatbotMapper.mapHistoryDocumentsToMessages(frame.data_history || []);

				if (frame.data_history && frame.data_history.length > 0) {
					const doc = frame.data_history[0];
					const count = doc?.message_notification_count ?? -1;
					let isNewMessage = false;

					const lastBotMsg = mappedMessages.slice().reverse().find((m) => m.sender === 'bot');

					// Si es la primera evaluación en memoria para este perfil, recuperar el ID leído de localStorage
					if (this.lastBotMessageId === null && this.currentIdentityFingerprint) {
						try {
							if (typeof localStorage !== 'undefined') {
								const storedReadId = localStorage.getItem(`inchat_last_read_${this.currentIdentityFingerprint}`);
								if (storedReadId) {
									this.lastBotMessageId = storedReadId;
								}
							}
						} catch (e) {}
					}

					const isLastBotUnread =
						!!lastBotMsg && (this.lastBotMessageId === null || lastBotMsg.id !== this.lastBotMessageId);

					if (count > 0) {
						// El socio envió consulta y está esperando respuesta del asesor (pendiente en CRM).
						// El socio no tiene mensajes pendientes de leer del asesor.
						isNewMessage = false;
					} else if (count === 0) {
						// count === 0: el asesor/bot atendió/respondió.
						if (this.lastNotificationCount > 0) {
							// Transición en vivo: de en espera (>0) a respondido (=0)
							isNewMessage = true;
						} else if (isLastBotUnread) {
							// Carga inicial o nuevo mensaje no leído aún por el socio
							isNewMessage = true;
						}
					} else {
						// Fallback si no viene message_notification_count en el frame
						if (isLastBotUnread && this.lastBotMessageId !== null) {
							isNewMessage = true;
						}
					}

					// Desencadenar la notificación
					if (isNewMessage) {
						this.unreadNotification$.next(true);
					} else if (count > 0) {
						this.unreadNotification$.next(false);
					}

					// Actualizar referencias para la próxima evaluación
					if (count !== -1) {
						this.lastNotificationCount = count;
					}
					// NOTA: No sobreescribir this.lastBotMessageId aquí.
					// Solo debe actualizarse en clearUnreadNotification() cuando el socio abre el chat.
				}

				// MERGE STRATEGY: Preserve optimistic messages that haven't arrived from the backend yet
				const currentOptimistic = this.currentMessages.filter((m) => m.optimistic);
				for (const optMsg of currentOptimistic) {
					if (optMsg.sender === 'user') {
						const existingBackendMsg = mappedMessages.find((m) => {
							if (m.sender !== 'user') return false;
							if (optMsg.text && m.text && m.text.trim() === optMsg.text.trim()) return true;
							if (optMsg.backendPayload && m.text && m.text.trim() === optMsg.backendPayload.trim()) return true;
							if (optMsg.attachment && m.attachment) {
								const timeDiff = Math.abs(
									(m.dateObj?.getTime() || 0) - (optMsg.dateObj?.getTime() || 0)
								);
								if (timeDiff < 120000) return true;
							}
							return false;
						});

						if (!existingBackendMsg) {
							mappedMessages.push(optMsg);
						} else {
							if (optMsg.backendPayload && existingBackendMsg.text && existingBackendMsg.text.trim() === optMsg.backendPayload.trim()) {
								existingBackendMsg.text = optMsg.text;
								existingBackendMsg.backendPayload = optMsg.backendPayload;
							}
							if (optMsg.attachment && existingBackendMsg.attachment) {
								if (
									!existingBackendMsg.attachment.name ||
									existingBackendMsg.attachment.name.length > 30
								) {
									existingBackendMsg.attachment.name = optMsg.attachment.name;
								}
							}
							if (optMsg.contextId) {
								existingBackendMsg.contextId = existingBackendMsg.contextId || optMsg.contextId;
								if (!existingBackendMsg.contextText && optMsg.contextText) {
									existingBackendMsg.contextText = optMsg.contextText;
								}
								if (!existingBackendMsg.contextName && optMsg.contextName) {
									existingBackendMsg.contextName = optMsg.contextName;
								}
							}
						}
					} else if (optMsg.sender === 'bot') {
						const existingBotMsg = mappedMessages.find((m) => {
							if (m.sender !== 'bot') return false;
							if (optMsg.text && m.text && m.text.trim() === optMsg.text.trim()) return true;
							return false;
						});

						if (!existingBotMsg) {
							mappedMessages.push(optMsg);
						}
					}
				}

				mappedMessages.sort((a, b) => {
					const timeA = a.dateObj ? a.dateObj.getTime() : null;
					const timeB = b.dateObj ? b.dateObj.getTime() : null;
					if (timeA !== null && timeB !== null) {
						if (timeA !== timeB) return timeA - timeB;
					}
					if (a.sender === 'user' && b.sender === 'bot') return -1;
					if (a.sender === 'bot' && b.sender === 'user') return 1;
					return 0;
				});

				this.messagesSubject.next(mappedMessages);
			},
			error: () => {
				if (this.connectionGeneration !== currentGeneration) return;
				if (!this.intentionalClose) {
					this.scheduleReconnect();
				}
			}
		});
	}

	public retryConnection(): void {
		this.disconnect();
		this.connectionStateSubject.next('disconnected');
		this.connect();
	}

	private handleInvalidDocument(): void {
		this.connectionStateSubject.next('error');
		this.disconnect();
	}

	private scheduleReconnect(): void {
		this.clearReconnectTimer();
		if (this.connectionStateSubject.value !== 'error') {
			this.connectionStateSubject.next('reconnecting');
			this.reconnectTimer = setTimeout(() => {
				this.cleanupWs();
				this.connect();
			}, 5000);
		}
	}

	private clearReconnectTimer(): void {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
	}

	private cleanupWs(): void {
		this.connectionGeneration++;
		if (this.wsSubscription) {
			this.wsSubscription.unsubscribe();
			this.wsSubscription = null;
		}
		if (this.wsSubject) {
			this.wsSubject.complete();
			this.wsSubject = null;
		}
	}

	public disconnect(): void {
		this.intentionalClose = true;
		this.clearReconnectTimer();
		this.cleanupWs();

		if (this.connectionStateSubject.value !== 'error') {
			this.connectionStateSubject.next('disconnected');
		}
	}

	public appendOptimisticMessage(msg: ChatMessageUI): void {
		if (msg.backendPayload && msg.text) {
			ChatbotMapper.registerButtonTitle(msg.backendPayload, msg.text);
		}
		if (msg.contextId && (msg.contextText || msg.contextName)) {
			ChatbotMapper.saveMessageContext(msg.text || msg.attachment?.name || '', {
				contextId: msg.contextId,
				contextText: msg.contextText,
				contextName: msg.contextName,
				timestamp: msg.dateObj?.getTime() || Date.now()
			});
		}
		const current = this.currentMessages;
		this.messagesSubject.next([...current, msg]);
	}

	public updateMessageStatus(id: string, status: 'sending' | 'sent' | 'read' | 'error'): void {
		const current = this.currentMessages;
		const updated = current.map((m) => (m.id === id ? { ...m, status } : m));
		this.messagesSubject.next(updated);
	}

	public async uploadFile(file: File): Promise<string> {
		const formData = new FormData();
		formData.append('file', file);

		const headers = new HttpHeaders().set('api-key', ChatbotConfig.API_KEY);

		const response = await firstValueFrom(
			this.http.post<ChatUploadResponse>(
				`${ChatbotConfig.API_BASE_URL}${ChatbotConfig.ENDPOINTS.UPLOAD}`,
				formData,
				{
					headers,
					context: new HttpContext().set(BYPASS_INTERCEPTOR, true)
				}
			)
		);

		if (!response) throw new Error('Respuesta nula');
		if (response.error === true) throw new Error(response.message || 'Error al subir');
		if (response.status === false) throw new Error(response.message || 'Error de estado');
		if (!response.data || !response.data.url || response.data.url.trim() === '') {
			throw new Error('URL vacía');
		}

		return response.data.url;
	}

	public async sendMessage(messageText: string, fileUrl: string, contextId: string = ''): Promise<void> {
		const phone = this.phone;
		if (!phone) throw new Error('Teléfono no disponible');

		const normalizedFileUrl = (fileUrl || '').trim();
		const trimmedMsg = (messageText || '').trim();
		if (!trimmedMsg && !normalizedFileUrl) {
			throw new Error('Debe enviar texto o un archivo');
		}

		const payload: ChatSendMessageRequest = {
			phone: phone,
			message: trimmedMsg,
			file_url: normalizedFileUrl,
			context_id: contextId,
			is_leader: this.isLeader
		};

		const authorization = this.getAuthorizationToken();
		if (!authorization) throw new Error('Token de autorización no disponible');

		const headers = new HttpHeaders()
			.set('api-key', ChatbotConfig.API_KEY)
			.set('authorization', authorization);

		const response = await firstValueFrom(
			this.http.post<ChatMessageResponse>(
				`${ChatbotConfig.API_BASE_URL}${ChatbotConfig.ENDPOINTS.MESSAGE}`,
				payload,
				{
					headers,
					context: new HttpContext().set(BYPASS_INTERCEPTOR, true)
				}
			)
		);

		if (!response) throw new Error('Respuesta nula');
		if (response.status === false) throw new Error(response.message || 'Error al enviar el mensaje');

		// Renderizado instantáneo de opciones y respuesta del bot recibidas en el payload HTTP
		if (response.json_model && Array.isArray(response.json_model) && response.json_model.length > 0) {
			const immediateBotMsgs = ChatbotMapper.mapJsonModelToBotMessages(response.json_model, response.type);
			if (immediateBotMsgs.length > 0) {
				const current = this.currentMessages;
				this.messagesSubject.next([...current, ...immediateBotMsgs]);
			}
		}
	}

	public refreshSession(): void {
		if (this.connectionStateSubject.value === 'connected' || this.connectionStateSubject.value === 'reconnecting') {
			this.cleanupWs();
			this.connect();
		}
	}

	public get currentMessages(): ChatMessageUI[] {
		return this.messagesSubject.getValue();
	}

	ngOnDestroy(): void {
		this.disconnect();
		this.userInfoSub?.unsubscribe();
	}
}
